import sortBy from 'lodash/sortBy';
import find from 'lodash/find';
import filter from 'lodash/filter';
import last from 'lodash/last';
import first from 'lodash/first';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import lodashMap from 'lodash/map';
import findLastIndex from 'lodash/findLastIndex';
import includes from 'lodash/includes';
import reject from 'lodash/reject';
import cloneDeep from 'lodash/cloneDeep';
import findLast from 'lodash/findLast';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import { of, zip } from 'rxjs';
import { share, map } from 'rxjs/operators';
import { Moment } from 'moment';

import { ILogEvent, LogViewModel, ILog, ILogRemark, IServerDriverGroup } from './log-view-model';

import { IMoment, moment } from '@lib/moment';
import { IServerCategorizedViolations } from '@lib/compliance-utils';
import { fullName, generateUuid, wrapInObject } from '@lib/utils';
import { DrivingPeriod } from '@lib/data/driving-period';
import { DRIVING_AG_EXCEPTION, ILogSuggestionData, IOdometerEntry, LogSuggestion } from '@misc/log-suggestion';

import { CANADA_CYCLES } from '../../cycle/cycles';

export const ADVERSE_DRIVING_EXCEPTION = 'adverse_driving';

export interface ILogObject {
  log: ILog;
}

export interface IAsset {
  id: number | null;
  name: string;
  groupIds?: number[];
}

interface IServerAsset {
  id: number;
  name: string;
  group_ids: number[];
}

interface IServerAssetObject {
  asset: IServerAsset;
}

export interface IServerDispatcher {
  id: number;
  first_name: string;
  last_name: string;
}

export interface IDispatcher {
  id: number;
  fullName: string;
}

export interface ILogVehicle {
  id: number;
  metricUnits?: boolean;
  number: string;
  groupIds?: number[];
  model?: string;
  make?: string;
  status?: string;
}

export interface IServerLogVehicle {
  id: number;
  metric_units: boolean;
  number: string;
  group_ids: number[];
  model?: string;
  make?: string;
  status?: string;
  prevent_auto_odometer_entry?: boolean;
}

export interface ILogServerData {
  date: string;
  id: number;
  driver: IServerDriver;
  categorized_violations: IServerCategorizedViolations;
  co_driver_full_names: string[];
  driver_first_name: string;
  driver_last_name: string;
  driver_company_id: string | number;
  time_zone: string;
  cycle: string;
  carrier_name: string;
  carrier_city: string;
  carrier_state: string;
  carrier_street: string;
  carrier_zip: string;
  ref_no: string;
  origin: string;
  destination: string;
  notes: string;
  total_miles: number;
  trailer_nums: string[];
  odometers: ILogsOdometer;
  terminal_city: string;
  terminal_state: string;
  terminal_street: string;
  terminal_zip: string;
  metric_units: boolean;
  short_haul: boolean;
  adverse_driving: boolean;
  exception_adverse_driving: boolean;
  exception_wait_time: boolean;
  exception_short_haul: boolean;
  exception_24_hour_restart: boolean;
  exception_8_hour_break: boolean;
  exception_ca_farm_school_bus: boolean;
  next_day_log_present: boolean;
  tractor_nums: string[];
  editable: boolean;
  log_suggestion: ILogSuggestionData;
  driver_signed_at: string;
  driver_signature_url: string;
  deferral_day: number;
  deferred_minutes: number;
  total_pc_meters: number;
  reduced_rest_day: number;
  reduced_rest_minutes: number
  inspection_reports: IServerLogInsepctionReport[];
  cycle_resets: Array<{
    cycle_reset: {
      status: string;
      id: number;
      offline_id: string;
      type: string;
      end_time: string;
    };
  }>;
  offline_id: string;
  edit_history: IServerLogEditsHistory[];
  violations: IServerLogsViolations[];
  eld_mode: string;
  vehicles: Array<{vehicle: IServerLogVehicle}>;
  assets: Array<{asset: IServerAsset}>;
  co_drivers: Array<{co_driver: IServerDriver}>;
  remarks: Array<{remark: IServerRemark}>;
  events: Array<{event: IServerEvent}>;
  event_durations?: {
    on_duty: number;
    driving: number;
  };
  total_defects?: number;
  total_inspection_reports?: number;
  multiple_log_suggestion?: boolean;
  vehicle_ids?: number[];
  exception_ag_driving?: boolean;
  coaching_status?: string;
}

interface IServerRemark {
  id?: number;
  offline_id?: string;
  time?: string | IMoment;
  notes: string;
  exception_log_offline_id?: string;
  location?: string;
  city?: string;
  state?: string;
}

export interface IServerEvent {
  id?: number;
  end_sds_eld_event_offline_id?: string;
  driving_period?: any;
  offline_id?: string;
  time?: IMoment | Moment | string;
  notes?: string;
  is_manual?: boolean;
  start_sds_eld_event_offline_id?: string;
  exception_log_offline_id?: string;
  source_driving_period_id?: string;
  isEldGenerated?: boolean;
  type?: string;
  location?: string;
  city?: string;
  state?: string;
  ignore_drive_time_duty_type?: string;
  ignore_drive_time_by?: IServerDispatcher;
  vehicle_number?: string;
  secondary_label?: string;
  eld_type?: string;
}

export interface ILogsOdometer {
  [key: string]: IOdometerEntry[]
}

export interface IServerDriver {
  id: number;
  first_name: string;
  last_name: string;
  driver_company_id: number | string;
  deactivated_at: number;
  email: string;
  groups?: IServerDriverGroup[];
  group_ids?: number[];
  manual_driving_enabled: boolean;
  split_in_progress?: boolean;
  eld_mode: string;
  minute_logs: boolean;
  trip_assignable_status?: string;
  app_version?: string;
}

export interface IServerLogInsepctionReport {
  inspection_report: {
    id: number;
    time: string;
    entity: {
      id: number;
      number: string;
    },
    entity_num: string;
    entity_type: string;
    odometer: number;
    inspected_parts: any;
    driver_signed_at: number;
    mechanic_signed_at: number;
    defects: string;
    vehicle: {
      id: number;
      number: string;
    }
  }
}

export interface IServerLogEditsHistory {
  id: number;
  status: string;
  reason: string
  suggested_at: string;
  dispatchers: IServerDispatcher[];
  approved_by: IServerDispatcher[];
  cancelled_by: IServerDispatcher[];
  rejected_by: IServerDispatcher[];
  voided_by: IServerDispatcher[];
}

export interface IServerLogsViolations {
  violation: {
    id: number;
    type: string;
    start_time: string;
    end_time: string;
  }
}

export interface ICoDriver {
  id: number;
  name: string;
  groupIds?: number[]
}

export interface IUpsertDutyStatusInfo {
  time: IMoment | Moment;
  endTime: IMoment | Moment;
  type: string;
  location: string;
  notes: string;
  isManual: boolean;
  isSds: boolean;
  isEldGenerated: boolean;
  offlineId?: string;
}

export interface IAuthEvent {
  id: number,
  eventType: string,
  time: IMoment,
}

export class Log {
  public data: ILogServerData;

  constructor(data: ILogServerData) {
    this.data = data;
  }

  get id(): number {
    return this.data.id;
  }

  get offlineId(): string {
    return this.data.offline_id;
  }

  get driverId(): number {
    return this.data.driver.id;
  }

  get isDriverEldEnabled(): boolean {
    return this.data.driver.eld_mode !== 'none';
  }

  get startTime() {
    const data = this.data;
    return moment.mapTz(data.date, 'YYYY-MM-DD', data.time_zone).startOf('day');
  }

  get endTime(): IMoment {
    return this.startTime.add(1, 'day');
  }

  get isEldEnabled(): boolean {
    return this.data.eld_mode !== 'none';
  }

  get minuteLogs(): boolean {
    return this.data.driver.minute_logs;
  }

  get shortHaul(): boolean {
    return this.data.short_haul;
  }

  set shortHaul(value) {
    this.data.short_haul = value;
  }

  get adverseDriving(): boolean {
    return this.data[ADVERSE_DRIVING_EXCEPTION];
  }

  set adverseDriving(value: boolean) {
    this.data[ADVERSE_DRIVING_EXCEPTION] = value;
  }

  get drivingAgException(): boolean {
    return this.data[DRIVING_AG_EXCEPTION];
  }

  set drivingAgException(value: boolean) {
    this.data[DRIVING_AG_EXCEPTION] = value;
  }

  get metricUnits(): boolean {
    return this.data.metric_units;
  }

  get odometers(): ILogsOdometer {
    return this.data.odometers;
  }

  get totalMiles(): number {
    return this.data.total_miles;
  }

  get totalPCMeters(): number {
    return this.data.total_pc_meters;
  }

  get cycle(): string {
    return this.data.cycle;
  }

  get hasCanadaCycle(): boolean {
    return CANADA_CYCLES.includes(this.cycle);
  }

  get vehicles(): ILogVehicle[] {
    return this.data.vehicles && this.data.vehicles.map((v: {vehicle: IServerLogVehicle}) => {
      const vehicle: IServerLogVehicle = v.vehicle;
      return {
        id: vehicle.id,
        number: vehicle.number,
        metricUnits: vehicle.metric_units,
        groupIds: vehicle.group_ids,
      };
    });
  }

  get multipleLogSuggestions(): boolean {
    return this.data.multiple_log_suggestion;
  }

  get logSuggestion(): LogSuggestion {
    return this.data.log_suggestion ? new LogSuggestion(this.data.log_suggestion) : null;
  }

  get assets(): IAsset[] {
    return this.data.assets.map((assetObject: IServerAssetObject) => {
      const asset: IServerAsset = assetObject.asset;
      return {
        id: asset.id,
        name: asset.name,
        groupIds: asset.group_ids,
      };
    });
  }

  get coDrivers(): ICoDriver[] {
    return this.data.co_drivers.map(d => {
      const coDriver = d.co_driver;
      return {
        id: coDriver.id,
        name: fullName(coDriver),
        groupIds: coDriver.group_ids,
      };
    });
  }

  get events(): ILogEvent[] {
    const data = this.data;
    const logStartTime = this.startTime;
    const logEndTime = this.endTime;
    const timeZone = data.time_zone;
    const now = moment.mapTz(timeZone);

    // TODO: Remove filter once auth events are incorporated
    // FILTER events with undefined type - Authentication Events
    const eventsData = data.events.filter(e => e.event.type);

    let events = eventsData.map((e, index) => {
      const event = e.event;
      const nextEvent = eventsData[index + 1];
      const startTime = moment(event.time).mapTz(timeZone);
      let endTime;

      if (nextEvent) {
        endTime = moment(nextEvent.event.time).mapTz(timeZone);
      } else if (startTime.isSameOrAfter(logEndTime)) {
        // Assume event ends at the end of next day (for events at day end boundary)
        endTime = startTime.clone().endOf('day');
      } else if (now.isBefore(logEndTime)) {
        endTime = now;
      } else {
        endTime = logEndTime;
      }

      if (event.driving_period) {
        event.driving_period.note = event.notes;
      }

      return {
        id: event.id,
        offlineId: event.offline_id,
        time: startTime,
        endTime,
        type: event.type,
        location: getLocation(event),
        notes: event.notes,
        drivingPeriod: event.driving_period ? new DrivingPeriod(event.driving_period) : null,
        sourceDrivingPeriodId: event.source_driving_period_id,
        startSdsEldEventOfflineId: event.start_sds_eld_event_offline_id,
        endSdsEldEventOfflineId: event.end_sds_eld_event_offline_id,
        isManual: event.is_manual,
        isUpdatedByDriver: event.secondary_label === 'driver',
        isEldGenerated: event.isEldGenerated,
        ignoredDriveTimeDispatcher: event.ignore_drive_time_by ? {
          id: event.ignore_drive_time_by.id,
          fullName: fullName(event.ignore_drive_time_by),
        } : undefined,
        ignoredDriveTimeDutyStatus: event.ignore_drive_time_duty_type,
        vehicleNum: event.vehicle_number,
      };
    });

    // Remove multiple events before or at start of day and multiple events at day end
    events = sortBy(events, e => e.time.unix());
    const eventBeforeStart = last(filter(events, e => e.time.isSameOrBefore(logStartTime)));
    const eventAfterEnd = first(filter(events, e => e.time.isSameOrAfter(logEndTime)));
    const eventsBetween = filter(events, e => e.time.isAfter(logStartTime) && e.time.isBefore(logEndTime));
    events = compact([eventBeforeStart, eventsBetween, eventAfterEnd]);
    return flatten(events);
  }

  public get authEvents(): IAuthEvent[] {
    const data: ILogServerData = this.data;
    const timeZone: string = data.time_zone;
    const authEvents: IServerEvent[] = data.events.filter(e => !e.event.type).map(e => e.event);
    return authEvents.map(e => {
      return {
        id: e.id,
        eventType: e.eld_type === 'login' ? $localize`LOG IN` : $localize`LOG OUT`,
        time: moment(e.time).mapTz(timeZone),
        itemType: 'authEvent',
      };
    });
  }

  get remarks(): ILogRemark[] {
    const timeZone = this.data.time_zone;
    return this.data.remarks.map(r => {
      const remark = r.remark;
      return {
        id: remark.id,
        offlineId: remark.offline_id,
        time: moment.mapTz(remark.time, timeZone),
        location: getLocation(remark),
        notes: remark.notes,
        exception_log_offline_id: remark.exception_log_offline_id,
      };
    });
  }

  get viewModel(): ILog {
    const viewModel = new LogViewModel(this);
    return viewModel.get();
  }

  public getEventByOfflineId(offlineId: string): ILogEvent {
    return this.events.find(e => e.offlineId === offlineId);
  }

  public getEventAt(time: IMoment | Moment): ILogEvent {
    return find(this.events, (e: ILogEvent) => e.time.isSameOrBefore(time) && e.endTime.isAfter(time));
  }

  public getEventStartingAt(time: IMoment): ILogEvent {
    return find(this.events, (e: ILogEvent) => e.time.isSame(time));
  }

  public getEventsBetween(start: IMoment | Moment, end: IMoment): ILogEvent[] {
    return filter(this.events, (e: ILogEvent) => e.time.isSameOrAfter(start) && e.time.isSameOrBefore(end));
  }

  public addEvent(eventData: IServerEvent): string {
    const events = this.data.events;
    const index = findLastIndex(events, e => moment(e.event.time).isSameOrBefore(eventData.time));
    const offlineId = generateUuid();
    const event: IServerEvent = {
      ...eventData,
      offline_id: offlineId,
      time: eventData.time && typeof eventData.time !== 'string' ? eventData.time.format() : null,
    };

    events.splice(index + 1, 0, { event });
    return offlineId;
  }

  public updateEvent(offlineId: string, eventData: any): void {
    // added any type because we need to mutate object
    let event: any = this.data.events.find(e => e.event.offline_id === offlineId);
    if (event) {
      event = event.event;
      for (const field of Object.keys(eventData)) {
        const value = field === 'time' ? eventData[field].format() : eventData[field];
        event[field] = value;
      }
    }
  }

  public deleteEventByOfflineId(offlineId: string): void {
    const data = this.data;
    const index = data.events.findIndex(e => e.event.offline_id === offlineId);
    if (index >= 0) data.events.splice(index, 1);
  }

  public updateIgnoreDriveTimeEvent(offlineId: string, status: string | null, dispatcher: IServerDispatcher): void {
    let event: any = this.data.events.find(e => e?.event.offline_id === offlineId);
    if (event) {
      event = event.event;
      event.ignore_drive_time_duty_type = status;
      event.ignore_drive_time_by = dispatcher;
    }
  }

  public upsertDutyStatus(item: IUpsertDutyStatusInfo): void {
    let offlineId = item.offlineId;
    let startTime = item.time;
    let endTime = item.endTime;

    const events = this.events;
    const event = events.find(e => e.offlineId === offlineId);
    const eventId = event && event.id;
    if (event) {
      startTime = moment.min(event.time, startTime);
      endTime = moment.max(event.endTime, endTime);
    }

    // Delete events in the middle
    const eventsToDeletePredicate = e => {
      return e.time.isSameOrAfter(startTime) && e.endTime.isSameOrBefore(endTime) && e.offlineId !== offlineId;
    };
    const eventsToDelete = events.filter(eventsToDeletePredicate);
    eventsToDelete.forEach(e => this.deleteEventByOfflineId(e.offlineId));

    const prevEvent = findLast(events, e => e.time.isBefore(startTime) && e.offlineId !== offlineId);
    const prevEventOfflineId = prevEvent && prevEvent.offlineId;
    const nextEvent = events.find(e => e.endTime.isAfter(endTime) && e.offlineId !== offlineId);
    const nextEventOfflineId = nextEvent && nextEvent.offlineId;
    const nextEventType = nextEvent && nextEvent.type;

    if (nextEvent && nextEventOfflineId !== prevEventOfflineId) {
      // Update next event's start time or merge it if it has the same type
      if (
        nextEvent.type === item.type
        && !(item.isEldGenerated
        || nextEvent.startSdsEldEventOfflineId
        || nextEvent.drivingPeriod
        || nextEvent.sourceDrivingPeriodId)) {
        if (endTime.isBefore(this.endTime)) {
          this.deleteEventByOfflineId(nextEvent.offlineId);
        }
      } else {
        this.updateEvent(nextEvent.offlineId, { time: item.endTime });
      }
    } else if (!nextEvent && item.endTime.isSameOrAfter(this.endTime) && !item.isEldGenerated) {
      // Add sentinel event at the end if there's no existing event at that time
      const lastEvent = last(events);
      this.addEvent({
        time: this.endTime,
        type: lastEvent.type,
        location: lastEvent.location,
        notes: lastEvent.notes,
        is_manual: lastEvent.isManual,
      });
    } else if (!eventId && !(nextEventOfflineId === prevEventOfflineId && nextEventType === item.type)) {
      // Create end event if new duty status was inserted
      const endEvent = nextEvent ? nextEvent : last(events);
      this.addEvent({ time: item.endTime, type: endEvent.type, is_manual: endEvent.isManual });
    }

    if (prevEvent && prevEventOfflineId !== nextEventOfflineId) {
      // Merge with previous event if it has the same type
      if (
        prevEvent.type === item.type
        && !(item.isEldGenerated
        || prevEvent.startSdsEldEventOfflineId
        || prevEvent.drivingPeriod
        || prevEvent.sourceDrivingPeriodId
      )) {
        if (prevEvent.time.isBefore(this.startTime)) {
          item.time = this.startTime;
        } else {
          item.time = prevEvent.time.clone();
          this.deleteEventByOfflineId(prevEventOfflineId);
        }
      }
    } else if (!prevEvent && item.time.isSame(this.startTime) && !item.isEldGenerated) {
      // Add sentinel event at start if there's no existing event at that time
      offlineId = this.addEvent({ time: this.startTime, type: item.type, is_manual: item.isManual });
    }

    // Form fields
    if (nextEventOfflineId === prevEventOfflineId && nextEventType === item.type) {
      this.updateEvent(nextEvent.offlineId, {
        location: item.location,
        notes: item.notes,
      });
    } else if (offlineId) {
      const eventData = {
        type: item.type,
        time: item.time,
        location: item.location,
        notes: item.notes,
        is_manual: item.isManual,
        isEldGenerated: item.isEldGenerated,
      };
      if (item.isEldGenerated) delete eventData['time']; // prevent time change when reclassifying eld generated event
      this.updateEvent(offlineId, eventData);
    } else {
      this.addEvent({
        type: item.type,
        time: item.time,
        location: item.location,
        notes: item.notes,
        is_manual: item.isManual,
      });
    }
  }

  public getRemarkByOfflineId(offlineId) {
    return this.remarks.find(r => r.offlineId === offlineId);
  }

  public addRemark(remarkData: IServerRemark): string {
    const remarks = this.data.remarks;
    const index = findLastIndex(remarks, r => moment(r.remark.time).isSameOrBefore(remarkData.time));
    const offlineId = generateUuid();
    const remark = {
      ...remarkData,
      offline_id: offlineId,
      time: remarkData.time && typeof remarkData.time !== 'string' ? remarkData.time.format() : null,
    };
    remarks.splice(index + 1, 0, { remark });
    return offlineId;
  }

  public updateRemark(offlineId: string, remarkData: any): void {
    // added any type because we need to mutate object
    let remark: any = this.data.remarks.find(r => r.remark.offline_id === offlineId);
    if (remark) {
      remark = remark.remark;
      for (const field of Object.keys(remarkData)) {
        const value = field === 'time' ? remarkData[field].format() : remarkData[field];
        remark[field] = value;
      }
    }
  }

  public deleteRemarkByOfflineId(offlineId: string): void {
    const index = this.data.remarks.findIndex(r => r.remark.offline_id === offlineId);
    this.data.remarks.splice(index, 1);
  }

  public upsertRemark(item: any, isAdverseDriving: boolean): void {
    const offlineId = item.offlineId;
    const remark = this.getRemarkByOfflineId(offlineId);
    const data = {
      time: item.time,
      notes: item.notes,
      location: item.location,
    };

    if (isAdverseDriving) {
      data['exception_log_offline_id'] = this.offlineId;
      this.adverseDriving = true;
    }

    if (remark) {
      this.updateRemark(offlineId, data);
    } else {
      this.addRemark(data);
    }
  }

  public applyLogSuggestion(logSuggestion, vehiclesSearch, driversSearch, assetsSearch) {
    // Log form changes
    const data = this.data;
    const driverId = this.driverId;
    const logChanges = logSuggestion.getLog(driverId, this.offlineId) || {};
    const endOfDay = this.endTime;
    const newItems$ = [];

    for (const field of Object.keys(logChanges)) {
      const change = logChanges[field];
      if (isArray(change)) {
        const value = change[1];
        data[field] = value;
        if (field === 'vehicle_ids') {
          if (!isEmpty(value)) {
            const wrapInVehicle = wrapInObject('vehicle');
            const vehicles$ = vehiclesSearch.getByIds(value).pipe(share());
            newItems$.push(vehicles$);
            vehicles$.subscribe(vehicles => {
              data.vehicles = vehicles.map(wrapInVehicle);
            });
          } else {
            data.vehicles = [];
          }
        } else if (field === 'asset_ids') {
          if (!isEmpty(value)) {
            const wrapInAsset = wrapInObject('asset');
            const assets$ = assetsSearch.getByIds(value).pipe(share());
            newItems$.push(assets$);
            assets$.subscribe(assets => {
              data.assets = assets.map(wrapInAsset);
            });
          } else {
            data.assets = [];
          }
        } else if (field === 'co_driver_ids') {
          if (!isEmpty(value)) {
            const wrapInCoDriver = wrapInObject('co_driver');
            const drivers$ = driversSearch.getByIds(value).pipe(share());
            newItems$.push(drivers$);
            drivers$.subscribe(users => {
              data.co_drivers = users.map(wrapInCoDriver);
            });
          } else {
            data.co_drivers = [];
          }
        }
      }
    }

    // Log event changes
    const eventsToDelete = [];
    const events = lodashMap(data.events, 'event');
    const eventChangesForDriver = logSuggestion.getEventChangesForDriver(driverId);
    for (const eventChanges of eventChangesForDriver) {
      const event = events.find(e => e.offline_id === eventChanges.offline_id);
      if (event) {
        // Existing event changes
        for (const field of Object.keys(eventChanges)) {
          const change = eventChanges[field];
          if (field === 'time' && change[1] === null) {
            eventsToDelete.push(event.id);
          } else if (!['offline_id', 'start_sds_eld_event_offline_id', 'end_sds_eld_event_offline_id'].includes(field)) {
            // Cater for events whose start time has been moved to end of day or new events created at end of day
            if (field === 'time' && endOfDay.isSameOrBefore(moment(change[1]))) {
              eventsToDelete.push(event.id);
            } else {
              event[field] = change[1];
            }
          }
        }
      } else if (eventChanges && eventChanges.time && eventChanges.time[0] === null && !!eventChanges.offline_id) {
        // New event
        const time = moment(eventChanges.time[1]);
        const prevEventIndex = findLastIndex(events, e => moment(e.time).isBefore(time));
        data.events.splice(prevEventIndex + 1, 0, {
          event: {
            offline_id: eventChanges.offline_id,
            time: eventChanges.time[1],
            type: eventChanges.type[1],
            notes: eventChanges.notes && eventChanges.notes[1],
            location: eventChanges.location && eventChanges.location[1],
            is_manual: eventChanges.is_manual && eventChanges.is_manual[1],
            source_driving_period_id: eventChanges.source_driving_period_id,
            start_sds_eld_event_offline_id: eventChanges.start_sds_eld_event_offline_id ?
              eventChanges.start_sds_eld_event_offline_id[1] : null,
            end_sds_eld_event_offline_id: eventChanges.end_sds_eld_event_offline_id ?
              eventChanges.end_sds_eld_event_offline_id[1] : null,
          },
        });
      }
    }
    let newEvents = reject(data.events, e => includes(eventsToDelete, e.event.id));
    newEvents = sortBy(newEvents, e => moment(e.event.time).unix());
    data.events = newEvents;

    // Log remarks changes
    const remarksToDelete = [];
    const remarks = lodashMap(data.remarks, 'remark');
    for (const remarkChanges of logSuggestion.getRemarkChangesForDriver(driverId)) {
      const remark = remarks.find(r => r.offline_id === remarkChanges.offline_id);
      // Existing remark changes
      if (remark) {
        for (const field of Object.keys(remarkChanges)) {
          const change = remarkChanges[field];
          if (field === 'time' && change[1] === null) {
            remarksToDelete.push(remark.id);
          } else if (field !== 'offline_id') {
            remark[field] = change[1];
          }
        }
      } else if (remarkChanges && remarkChanges.time && remarkChanges.time[0] === null) {
        const time = moment(remarkChanges.time[1]);
        const prevRemarkIndex = findLastIndex(remarks, r => moment(r.time).isBefore(time));
        data.remarks.splice(prevRemarkIndex + 1, 0, {
          remark: {
            offline_id: remarkChanges.offline_id,
            time: remarkChanges.time[1],
            notes: remarkChanges.notes && remarkChanges.notes[1],
            location: remarkChanges.location && remarkChanges.location[1],
            exception_log_offline_id: remarkChanges.exception_log_offline_id && remarkChanges.exception_log_offline_id[1],
          },
        });
      }
    }
    data.remarks = reject(data.remarks, r => includes(remarksToDelete, r.remark.id));

    if (!isEmpty(newItems$)) {
      return zip(...newItems$).pipe(map(() => this));
    } else {
      return of(this);
    }
  }

  public clone(): Log {
    const proto = Object.getPrototypeOf(this);
    const clone = Object.create(proto);
    clone.data = cloneDeep(this.data);
    return clone;
  }
}

// Helper functions

// Get location from either location property if present otherwise city/state properties
function getLocation(item): string {
  const location = item.location ? item.location : compact([item.city, item.state]).join(', ');
  // if city/state is empty return the correct falsy value
  return location ? location : item.location;
}
